// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The field view the type inspector and Tabel › Typer show
 * (`bygning/fieldView`): `?fields=` when the URL has one (a deep link wins),
 * else the project's remembered choice (`kortlaegning/templatePick`), else
 * the screening seed. A pick goes in the URL and is remembered for the
 * project, so both surfaces — and the next visit — follow it.
 */

import { useMemo, useState } from 'react';

import type { Template } from '../../api/types';
import {
  fieldViewOptions,
  formatFieldView,
  parseFieldView,
  resolveFieldView,
  viewTemplate,
  type FieldView,
} from '../../bygning/fieldView';
import type { Selection } from '../../bygning/selection';
import { readStoredFieldView, writeStoredFieldView, type ProjectIdentity } from '../../kortlaegning/templatePick';
import { useCaseShell } from './CaseShellContext';
import type { CaseData } from './useCaseData';

export interface FieldViewState {
  view: FieldView;
  /** The view's template; null for Alle felter. */
  template: Template | null;
  /** The picker's options (`fieldViewOptions`). */
  options: { value: string; label: string }[];
  /** Pick a view by its wire form (an option's value); junk is ignored. */
  choose: (raw: string) => void;
}

export function useFieldView(data: CaseData, sel: Selection, setSel: (patch: Partial<Selection>) => void): FieldViewState {
  const shell = useCaseShell();
  const identity: ProjectIdentity = useMemo(
    () => ({ id: data.project?.id ?? null, name: shell.projectName ?? '' }),
    [data.project?.id, shell.projectName],
  );
  // Bumped on a pick so the remembered choice is read again (a later URL without `?fields=` follows it).
  const [written, setWritten] = useState(0);
  const stored = useMemo(() => readStoredFieldView(identity), [identity, written]);
  // By wire form, so a re-parsed URL with the same choice keeps `view` (and what is memoised on it).
  const wanted = sel.fieldView === null ? null : formatFieldView(sel.fieldView);
  const view = useMemo(
    () => resolveFieldView(parseFieldView(wanted) ?? stored, data.templates),
    [wanted, stored, data.templates],
  );
  const options = useMemo(() => fieldViewOptions(data.templates), [data.templates]);

  return {
    view,
    template: viewTemplate(view, data.templates),
    options,
    choose: (raw) => {
      const next = parseFieldView(raw);
      if (next === null) return;
      setSel({ fieldView: next });
      writeStoredFieldView(identity, next);
      setWritten((n) => n + 1);
    },
  };
}
